<template>
  <template v-if="auctionStatus === AuctionStatuses.ENDED">
    <div class="lot-information-block auction-status-block box-shadow">
      <div class="auction-status-wrapper">
        <div class="title">{{ trans("live.auction_states.closed.title") }}</div>
        <a :href="auctionStore.routes['site.auction.show']" class="link-dashed">{{ trans("live.auction_states.closed.view_results") }}</a>
        <a :href="auctionStore.routes['profile.invoices.index']" class="link-dashed">{{ trans("live.auction_states.closed.my_purchases") }}</a>
        <a :href="auctionStore.routes['profile.messages.index']" class="link-dashed">{{ trans("live.auction_states.closed.contact_auctioneer") }}</a>
      </div>
    </div>
  </template>

  <template v-else>
    <div class="lot-information-block box-shadow">
      <div class="current-lot-image-block">
        <lot-gallery class="buyer-gallery"></lot-gallery>
        <div class="lot-favorite-mob">
          <favorite-button
            :lot-id-prop="activeLot?.id"
            :subscribers-count-prop="activeLot?.subscribers_count"
            show-count
          ></favorite-button>
        </div>
        <div id="history-mobile-wrapper">
          <div class="lot-timeline-messages"></div>
        </div>
      </div>
      <div class="mobile-place-bid-wrapper">
        <div class="mobile-place-bid-block">
          <div class="current-bid-anim">
            <transition name="current-bid">
              <div class="current-bid" :key="currentBid ?? 0">
                {{ format_currency(currentBid) }}
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div class="current-lot-info">
        <div class="lot-number">{{ trans("live.common.main_info.lot_number", { number: activeLot?.lot_number }) }}</div>
        <h1 class="lot-title">
          {{ activeLot?.title }}
        </h1>
        <custom-scrollbar class="custom-scrollbar mini lot-description-scrollbar controller-scrollbar">
          <div class="lot-description" v-html="activeLot?.description"></div>
        </custom-scrollbar>
        <div v-if="activeLot?.note" class="lot-description">
          {{ trans("live.common.main_info.note") }}:
          <span class="value">{{ activeLot?.note }}</span>
        </div>
        <div class="metas">
          <div v-if="catalog" class="metas__row catalog">
            {{ trans("live.common.main_info.catalog") }}:
            <span class="value">{{ catalog }}</span>
          </div>
          <div v-if="catalogValue" class="metas__row catalog-value">
            {{ trans("live.common.main_info.catalog_value") }}:
            <span class="value">{{ catalogValue }}</span>
          </div>
          <div v-if="condition || activeLot?.condition_notes" class="metas__row condition">
            {{ trans("live.common.main_info.conditions") }}:
            <span v-if="condition" class="value">{{ condition }}.</span>
            <span>{{ activeLot?.condition_notes }}</span>
          </div>
        </div>
        <div class="lot-info-bottom">
          <div class="bids-count">{{ trans("live.common.main_info.bids", { bids: activeLot?.bids_count }) }}</div>
          <favorite-button
            :lot-id-prop="activeLot?.id"
            :subscribers-count-prop="activeLot?.subscribers_count"
            show-count
          ></favorite-button>
        </div>
      </div>
      <div class="mobile-current-lot-info">
        <div class="lot-description" v-html="activeLot?.description"></div>
        <div class="metas">
          <div v-if="catalog" class="metas__row catalog">
            {{ trans("live.common.main_info.catalog") }}:
            <span class="value">{{ catalog }}</span>
          </div>
          <div v-if="catalogValue" class="metas__row catalog-value">
            {{ trans("live.common.main_info.catalog_value") }}:
            <span class="value">{{ catalogValue }}</span>
          </div>
          <div v-if="condition || activeLot?.condition_notes" class="metas__row condition">
            {{ trans("live.common.main_info.conditions") }}:
            <span v-if="condition" class="value">{{ condition }}.</span>
            <span>{{ activeLot?.condition_notes }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
  import { computed } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { formatCatalogValueRange, humanizedCatalog, humanizedCondition } from "@/utils/lot";
  import { AuctionStatuses } from "@/enum";
  import { trans } from "@/utils/trans";
  import FavoriteButton from "@/components/_partials/FavoriteButton.vue";
  import LotGallery from "@/components/_partials/LotGallery.vue";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import { format_currency } from "@/utils/currencies";

  const auctionStore = useAuctionStore();
  const auctionStatus = computed(() => auctionStore.auction.status);

  const activeLot = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot : auctionStore.fakeActiveLot));
  const catalog = computed(() => (activeLot.value ? humanizedCatalog(activeLot.value) : null));
  const condition = computed(() => (activeLot.value ? humanizedCondition(activeLot.value) : null));
  const catalogValue = computed(() => (activeLot.value ? formatCatalogValueRange(activeLot.value) : null));

  const currentBid = computed(() => activeLot.value?.current_bid);
</script>
